import { useState } from 'react';

import { deriveSlot, formatDateKeyBR, typeLabel } from './agenda-adapter';

import type { AppointmentWireItem } from '../../lib/api-client';
import type { JSX } from 'react';

/**
 * RescheduleModal — reagenda (cria sucessor + `rescheduledFromId`). Idioma de
 * modal do atendente. Data/hora nova + razão. Limite real validado pelo backend
 * (422); aviso honesto sem fabricar contador de chain (D2/D5).
 */
export function RescheduleModal({
  open,
  appt,
  submitting,
  onClose,
  onConfirm,
}: {
  open: boolean;
  appt: AppointmentWireItem;
  submitting: boolean;
  onClose: () => void;
  onConfirm: (p: { newScheduledAt: string; reason?: string }) => void;
}): JSX.Element | null {
  const slot = deriveSlot(appt);
  const [date, setDate] = useState(slot.dateKey);
  const [time, setTime] = useState(slot.start);
  const [reason, setReason] = useState('');

  if (!open) return null;
  const canConfirm = date.length > 0 && time.length > 0 && !submitting;

  const handleConfirm = (): void => {
    // date (YYYY-MM-DD) + time (HH:MM) interpretados em hora local → ISO.
    const local = new Date(`${date}T${time}`);
    const trimmed = reason.trim();
    onConfirm(
      trimmed.length > 0
        ? { newScheduledAt: local.toISOString(), reason: trimmed }
        : { newScheduledAt: local.toISOString() },
    );
  };

  return (
    <div
      role="dialog"
      aria-label="Reagendar agendamento"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div
        className="w-full max-w-lg rounded-lg p-6 shadow-xl"
        style={{ background: 'var(--bg-surface)' }}
      >
        <h2 className="mb-1 text-lg font-semibold">Reagendar agendamento</h2>
        <p className="mb-1 text-sm" style={{ color: 'var(--fg-3)' }}>
          {typeLabel(appt.type)} · {appt.title}
        </p>
        <p className="mono mb-4 text-xs" style={{ color: 'var(--fg-3)' }}>
          Atual: {formatDateKeyBR(slot.dateKey)} · {slot.start}–{slot.end}
        </p>

        <div className="mb-3 grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1 block text-sm font-medium">Nova data</span>
            <input
              type="date"
              className="w-full rounded border px-2 py-1"
              style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium">Nova hora</span>
            <input
              type="time"
              className="w-full rounded border px-2 py-1"
              style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
              value={time}
              onChange={(e) => setTime(e.target.value)}
            />
          </label>
        </div>

        <label className="mb-3 block">
          <span className="mb-1 block text-sm font-medium">Motivo (opcional)</span>
          <textarea
            className="w-full rounded border px-2 py-1"
            style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
            rows={3}
            maxLength={500}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Ex.: cliente solicitou remarcar por conflito de agenda."
          />
        </label>

        <p className="mb-3 text-xs" style={{ color: 'var(--fg-3)' }}>
          Reagendamentos são limitados — após o limite, o sistema pode exigir aprovação do
          supervisor.
        </p>

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            className="rounded border px-4 py-2 text-sm"
            style={{ borderColor: 'var(--border-default)' }}
            onClick={onClose}
            disabled={submitting}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={handleConfirm}
            disabled={!canConfirm}
          >
            {submitting ? 'Reagendando…' : 'Confirmar reagendamento'}
          </button>
        </div>
      </div>
    </div>
  );
}
